import React from "react";
import { interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { C, FONT, bd } from "./brand";

/** Barber pole khas aplikasinya: garis diagonal merah-teal-putih yang berputar. */
export const Pole: React.FC<{
  height?: number;
  width?: number;
  period?: number;
  style?: React.CSSProperties;
}> = ({ height = 260, width = 46, period = 22, style }) => {
  const frame = useCurrentFrame();
  const p = period;
  const step = p * 1.41421356;
  const pos = ((frame / 72) * step) % step;

  return (
    <div
      style={{
        width,
        height,
        borderRadius: width / 2,
        border: bd(4),
        backgroundImage: `repeating-linear-gradient(45deg, ${C.red} 0 ${p * 0.25}px, #fff ${p * 0.25}px ${p * 0.5}px, ${C.blue} ${p * 0.5}px ${p * 0.75}px, #fff ${p * 0.75}px ${p}px)`,
        backgroundSize: `100% ${step}px`,
        backgroundPositionY: pos,
        boxShadow: `5px 5px 0 ${C.ink}`,
        position: "relative",
        overflow: "hidden",
        ...style,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          background:
            "linear-gradient(90deg, rgba(0,0,0,0.3), rgba(255,255,255,0.45) 30%, rgba(255,255,255,0) 60%, rgba(0,0,0,0.28))",
        }}
      />
    </div>
  );
};

/** Kartu putih neo-brutalism. */
export const Card: React.FC<{
  children: React.ReactNode;
  bg?: string;
  padding?: number;
  radius?: number;
  shadow?: number;
  border?: number;
  style?: React.CSSProperties;
}> = ({
  children,
  bg = C.white,
  padding = 28,
  radius = 22,
  shadow = 8,
  border = 4,
  style,
}) => (
  <div
    style={{
      background: bg,
      border: bd(border),
      borderRadius: radius,
      boxShadow: `${shadow}px ${shadow}px 0 ${C.ink}`,
      padding,
      boxSizing: "border-box",
      ...style,
    }}
  >
    {children}
  </div>
);

/** Label kecil bergaya tombol/chip. */
export const Chip: React.FC<{
  children: React.ReactNode;
  bg?: string;
  color?: string;
  size?: number;
  style?: React.CSSProperties;
}> = ({ children, bg = C.yellow, color = C.ink, size = 26, style }) => (
  <span
    style={{
      display: "inline-block",
      background: bg,
      color,
      border: bd(3),
      borderRadius: 10,
      boxShadow: `3px 3px 0 ${C.ink}`,
      padding: `${size * 0.28}px ${size * 0.6}px`,
      fontSize: size,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: 2,
      fontFamily: FONT,
      ...style,
    }}
  >
    {children}
  </span>
);

/** Judul scene: muncul dari bawah dengan spring. */
export const SceneHeading: React.FC<{
  kicker: string;
  kickerBg?: string;
  title: string;
  size?: number;
}> = ({ kicker, kickerBg = C.yellow, title, size = 62 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const rise = spring({ frame, fps, config: { damping: 16, stiffness: 90 } });
  const y = interpolate(rise, [0, 1], [40, 0]);

  return (
    <div style={{ textAlign: "center", transform: `translateY(${y}px)`, opacity: rise }}>
      <Chip bg={kickerBg}>{kicker}</Chip>
      <div
        style={{
          fontFamily: FONT,
          color: C.ink,
          fontSize: size,
          fontWeight: 800,
          lineHeight: 1.1,
          letterSpacing: -1.5,
          marginTop: 22,
        }}
      >
        {title}
      </div>
    </div>
  );
};

/** Angka yang berhitung naik (total kasir & statistik laporan). */
export const Counter: React.FC<{
  to: number;
  durationInFrames?: number;
  prefix?: string;
  format?: (n: number) => string;
  delay?: number;
  style?: React.CSSProperties;
}> = ({ to, durationInFrames = 45, prefix = "", format, delay = 0, style }) => {
  const frame = useCurrentFrame();
  const value = interpolate(frame - delay, [0, durationInFrames], [0, to], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const text = format ? format(value) : Math.round(value).toLocaleString("id-ID");
  return (
    <span style={{ fontVariantNumeric: "tabular-nums", ...style }}>
      {prefix}
      {text}
    </span>
  );
};

/** Baris layanan di kartu kasir. */
export const ServiceRow: React.FC<{
  name: string;
  price: string;
  index: number;
  startAt: number;
  accent?: string;
}> = ({ name, price, index, startAt, accent = C.blue }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const at = startAt + index * 9;
  const s = spring({ frame: frame - at, fps, config: { damping: 15, stiffness: 110 } });
  const x = interpolate(s, [0, 1], [-70, 0]);
  const tick = interpolate(frame, [at + 18, at + 30], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        background: C.white,
        border: bd(3),
        borderRadius: 14,
        padding: "14px 20px",
        marginBottom: 12,
        boxShadow: `4px 4px 0 ${C.ink}`,
        transform: `translateX(${x}px)`,
        opacity: s,
        fontFamily: FONT,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            border: bd(3),
            background: accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 800,
            fontSize: 24,
            color: C.ink,
            transform: `scale(${tick})`,
          }}
        >
          ✓
        </div>
        <span style={{ fontSize: 32, fontWeight: 700, color: C.ink }}>{name}</span>
      </div>
      <span style={{ fontSize: 32, fontWeight: 800, color: C.ink }}>{price}</span>
    </div>
  );
};

export const In: React.FC<{
  children: React.ReactNode;
  delay?: number;
  from?: "left" | "right" | "bottom" | "top";
  style?: React.CSSProperties;
}> = ({ children, delay = 0, from = "bottom", style }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const s = spring({ frame: frame - delay, fps, config: { damping: 15, stiffness: 110 } });
  const d = interpolate(s, [0, 1], [90, 0]);
  const t =
    from === "left"
      ? `translateX(${-d}px)`
      : from === "right"
        ? `translateX(${d}px)`
        : from === "top"
          ? `translateY(${-d}px)`
          : `translateY(${d}px)`;
  return <div style={{ transform: t, opacity: s, ...style }}>{children}</div>;
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
